import React, { useState, useEffect } from 'react';
import {
    listProjectCredentials,
    createProjectCredential,
    deleteCredential,
    testCredential
} from '../../services/api';
import type { ProjectCredential, PlatformType } from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { GlassBadge } from '../ui/GlassBadge';
import { FileText, Linkedin, Twitter, Users, Instagram, Mail, Globe } from 'lucide-react';
import './SchedulingPage.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface Props {
    projectId: string;
}

const PlatformCredentials: React.FC<Props> = ({ projectId }) => {
    const [credentials, setCredentials] = useState<ProjectCredential[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState<PlatformType>('wordpress');
    const [formData, setFormData] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [testing, setTesting] = useState<string | null>(null);

    useEffect(() => {
        loadCredentials();
    }, [projectId]);

    // Listen for OAuth popup completion
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'oauth_success') {
                console.log('OAuth success:', event.data);
                loadCredentials(); // Refresh credentials list
                setShowAddForm(false);
            } else if (event.data.type === 'oauth_error') {
                console.error('OAuth error:', event.data);
                setError(event.data.message || 'OAuth authentication failed');
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const loadCredentials = async () => {
        try {
            setLoading(true);
            setError(null);
            const creds = await listProjectCredentials(projectId);
            setCredentials(creds);
        } catch (err: any) {
            console.error('Failed to load credentials:', err);
            setError(getApiError(err, 'Failed to load credentials'));
        } finally {
            setLoading(false);
        }
    };

    const handlePlatformChange = (platform: PlatformType) => {
        setSelectedPlatform(platform);
        setFormData({});
    };

    const handleOAuthConnect = (platform: PlatformType) => {
        // Open OAuth authorization in popup window
        const width = 600;
        const height = 700;
        const left = Math.floor((window.innerWidth - width) / 2);
        const top = Math.floor((window.innerHeight - height) / 2);

        const authUrl = `/api/oauth/${platform}/authorize?project_id=${projectId}`;

        window.open(
            authUrl,
            'oauth_popup',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
        );
    };

    const isOAuthPlatform = (platform: PlatformType): boolean => {
        return ['linkedin', 'twitter', 'facebook', 'instagram'].includes(platform);
    };

    const getFormFields = (platform: PlatformType): { name: string; label: string; type: string; placeholder: string }[] => {
        switch (platform) {
            case 'wordpress':
                return [
                    { name: 'site_url', label: 'Site URL', type: 'url', placeholder: 'https://example.com' },
                    { name: 'username', label: 'Username', type: 'text', placeholder: 'admin' },
                    { name: 'app_password', label: 'Application Password', type: 'text', placeholder: 'xxxx xxxx xxxx xxxx xxxx xxxx' }
                ];
            case 'linkedin':
                return [
                    { name: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Your LinkedIn client ID' },
                    { name: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Your LinkedIn client secret' },
                    { name: 'access_token', label: 'Access Token', type: 'text', placeholder: 'Your access token' }
                ];
            case 'twitter':
                return [
                    { name: 'bearer_token', label: 'Bearer Token', type: 'text', placeholder: 'Your Twitter bearer token' },
                    { name: 'api_key', label: 'API Key', type: 'text', placeholder: 'Your API key' },
                    { name: 'api_secret', label: 'API Secret', type: 'password', placeholder: 'Your API secret' }
                ];
            case 'facebook':
            case 'instagram':
                return [
                    { name: 'app_id', label: 'App ID', type: 'text', placeholder: 'Your Facebook app ID' },
                    { name: 'app_secret', label: 'App Secret', type: 'password', placeholder: 'Your app secret' },
                    { name: 'page_access_token', label: 'Page Access Token', type: 'text', placeholder: 'Your page access token' },
                    { name: 'page_id', label: 'Page ID', type: 'text', placeholder: 'Your page ID' }
                ];
            case 'edm':
                return [
                    { name: 'note', label: 'Note', type: 'text', placeholder: 'Uses existing SuiteCRM credentials' }
                ];
            default:
                return [];
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await createProjectCredential(projectId, selectedPlatform, formData);
            setShowAddForm(false);
            setFormData({});
            await loadCredentials();
        } catch (err: any) {
            console.error('Failed to create credential:', err);
            setError(getApiError(err, 'Failed to create credential'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleTest = async (credentialId: string) => {
        setTesting(credentialId);
        setError(null);

        try {
            const result = await testCredential(credentialId);
            if (result.success) {
                alert(`✓ Connection successful!\n\n${result.message}`);
            } else {
                alert(`✗ Connection failed!\n\n${result.message}`);
            }
            await loadCredentials();
        } catch (err: any) {
            console.error('Failed to test credential:', err);
            alert(`✗ Connection test failed!\n\n${getApiError(err, 'An error occurred')}`);
        } finally {
            setTesting(null);
        }
    };

    const handleDelete = async (credentialId: string, platform: string) => {
        if (!window.confirm(`Are you sure you want to delete the ${platform} credentials?`)) {
            return;
        }

        try {
            await deleteCredential(credentialId);
            await loadCredentials();
        } catch (err: any) {
            console.error('Failed to delete credential:', err);
            setError(getApiError(err, 'Failed to delete credential'));
        }
    };

    const getPlatformIcon = (platform: PlatformType): React.ReactNode => {
        const iconProps = { size: 16 };
        switch (platform) {
            case 'wordpress': return <FileText {...iconProps} />;
            case 'linkedin': return <Linkedin {...iconProps} />;
            case 'twitter': return <Twitter {...iconProps} />;
            case 'facebook': return <Users {...iconProps} />;
            case 'instagram': return <Instagram {...iconProps} />;
            case 'edm': return <Mail {...iconProps} />;
            default: return <Globe {...iconProps} />;
        }
    };

    const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
        switch (status) {
            case 'active':
                return 'success';
            case 'expired':
                return 'warning';
            case 'error':
                return 'danger';
            default:
                return 'default';
        }
    };

    const platformOptions = [
        { value: 'wordpress', label: 'WordPress' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'twitter', label: 'Twitter / X' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'edm', label: 'EDM (Email)' },
    ];

    if (loading) {
        return (
            <div className="credentials-loading">
                <GlassCard variant="default" padding="lg">
                    <div className="loading">Loading credentials...</div>
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="platform-credentials">
            <div className="credentials-header">
                <h2>Platform Credentials</h2>
                <GlassButton
                    variant={showAddForm ? 'secondary' : 'primary'}
                    onClick={() => setShowAddForm(!showAddForm)}
                >
                    {showAddForm ? 'Cancel' : '+ Add Credentials'}
                </GlassButton>
            </div>

            {error && (
                <GlassCard variant="default" padding="md" className="error-card" style={{ marginBottom: '1rem' }}>
                    <div className="error-message">{error}</div>
                </GlassCard>
            )}

            {showAddForm && (
                <GlassCard variant="strong" padding="lg" className="add-credential-card">
                    <h3>Add Platform Credentials</h3>

                    <div className="platform-selector">
                        <GlassSelect
                            label="Platform"
                            options={platformOptions}
                            value={selectedPlatform}
                            onChange={(value) => handlePlatformChange(value as PlatformType)}
                        />
                    </div>

                    {isOAuthPlatform(selectedPlatform) ? (
                        <div className="oauth-connect-section">
                            <p className="oauth-instructions">
                                Click the button below to connect your {selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1)} account via OAuth.
                                A popup window will open for you to authorize the connection.
                            </p>
                            <div className="form-actions">
                                <GlassButton
                                    variant="primary"
                                    onClick={() => handleOAuthConnect(selectedPlatform)}
                                    style={{
                                        background: selectedPlatform === 'linkedin' ? '#0077b5' :
                                                   selectedPlatform === 'twitter' ? '#1da1f2' :
                                                   selectedPlatform === 'facebook' ? '#1877f2' : '#e4405f',
                                        borderColor: selectedPlatform === 'linkedin' ? '#0077b5' :
                                                    selectedPlatform === 'twitter' ? '#1da1f2' :
                                                    selectedPlatform === 'facebook' ? '#1877f2' : '#e4405f',
                                    }}
                                >
                                    Connect {selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1)}
                                </GlassButton>
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setFormData({});
                                    }}
                                >
                                    Cancel
                                </GlassButton>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="credential-form">
                            {getFormFields(selectedPlatform).map((field) => (
                                <GlassInput
                                    key={field.name}
                                    label={field.label}
                                    type={field.type as 'text' | 'password' | 'url'}
                                    placeholder={field.placeholder}
                                    value={formData[field.name] || ''}
                                    onChange={(value) => setFormData({ ...formData, [field.name]: value })}
                                    disabled={field.name === 'note'}
                                />
                            ))}

                            <div className="form-actions">
                                <GlassButton
                                    type="submit"
                                    variant="primary"
                                    loading={submitting}
                                    disabled={submitting}
                                >
                                    Save Credentials
                                </GlassButton>
                                <GlassButton
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setFormData({});
                                    }}
                                >
                                    Cancel
                                </GlassButton>
                            </div>
                        </form>
                    )}
                </GlassCard>
            )}

            <div className="credentials-list">
                {credentials.length === 0 ? (
                    <GlassCard variant="default" padding="lg" className="empty-credentials-card">
                        <div className="empty-state">
                            <h3>No credentials yet</h3>
                            <p>Add credentials to start scheduling content for automatic publication.</p>
                        </div>
                    </GlassCard>
                ) : (
                    <div className="credentials-grid">
                        {credentials.map((cred) => (
                            <GlassCard key={cred.credential_id} variant="default" padding="md" className="credential-card">
                                <div className="credential-header">
                                    <div className="credential-title">
                                        <span className="platform-icon">{getPlatformIcon(cred.platform)}</span>
                                        <h3>{cred.platform.toUpperCase()}</h3>
                                    </div>
                                    <GlassBadge variant={getStatusBadgeVariant(cred.status)}>
                                        {cred.status}
                                    </GlassBadge>
                                </div>

                                <div className="credential-info">
                                    {cred.last_verified_at && (
                                        <p className="verified-at">
                                            Last verified: {new Date(cred.last_verified_at).toLocaleString()}
                                        </p>
                                    )}
                                    {cred.error_message && (
                                        <p className="error-text">
                                            Error: {cred.error_message}
                                        </p>
                                    )}
                                </div>

                                <div className="credential-actions">
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleTest(cred.credential_id)}
                                        loading={testing === cred.credential_id}
                                        disabled={testing === cred.credential_id}
                                    >
                                        Test Connection
                                    </GlassButton>
                                    <GlassButton
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleDelete(cred.credential_id, cred.platform)}
                                    >
                                        Delete
                                    </GlassButton>
                                </div>
                            </GlassCard>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .credentials-loading {
                    padding: 2rem;
                }

                .credentials-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .credentials-header h2 {
                    margin: 0;
                    font-size: var(--text-xl);
                    font-weight: var(--font-semibold);
                    color: var(--text);
                }

                .add-credential-card {
                    margin-bottom: 1.5rem;
                }

                .add-credential-card h3 {
                    margin: 0 0 1rem 0;
                    font-size: var(--text-lg);
                    font-weight: var(--font-semibold);
                    color: var(--text);
                }

                .platform-selector {
                    margin-bottom: 1.5rem;
                }

                .oauth-connect-section {
                    padding: 1rem 0;
                }

                .oauth-instructions {
                    color: var(--text-secondary);
                    margin-bottom: 1rem;
                    line-height: 1.5;
                }

                .credential-form {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .form-actions {
                    display: flex;
                    gap: 0.75rem;
                    margin-top: 0.5rem;
                }

                .empty-credentials-card {
                    text-align: center;
                }

                .empty-state h3 {
                    margin: 0 0 0.5rem 0;
                    color: var(--text);
                }

                .empty-state p {
                    margin: 0;
                    color: var(--text-secondary);
                }

                .credentials-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 1rem;
                }

                .credential-card {
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .credential-card:hover {
                    transform: translateY(-2px);
                    box-shadow: var(--shadow-lg);
                }

                .credential-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .credential-title {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .platform-icon {
                    font-size: 1.25rem;
                }

                .credential-title h3 {
                    margin: 0;
                    font-size: var(--text-base);
                    font-weight: var(--font-semibold);
                    color: var(--text);
                }

                .credential-info {
                    margin-bottom: 1rem;
                }

                .verified-at {
                    color: var(--text-secondary);
                    font-size: var(--text-sm);
                    margin: 0;
                }

                .error-text {
                    color: var(--danger);
                    font-size: var(--text-sm);
                    margin: 0.5rem 0 0 0;
                }

                .credential-actions {
                    display: flex;
                    gap: 0.5rem;
                }

                @media (max-width: 768px) {
                    .credentials-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: flex-start;
                    }

                    .credentials-grid {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default PlatformCredentials;
